import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { Profile } from '../lib/supabase';
import type { User } from '@supabase/supabase-js';

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, fullName: string, role: string, phone?: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const clearSession = async () => {
    setUser(null);
    setProfile(null);
    setLoading(false);
    try {
      await supabase.auth.signOut();
    } catch {
      // ignore sign out errors when clearing invalid session
    }
  };

  useEffect(() => {
    supabase.auth
      .getSession()
      .then(({ data: { session }, error }) => {
        if (error) {
          console.error('Session error:', error);
          const isTokenError =
            error.message?.toLowerCase().includes('refresh token') ||
            error.message?.toLowerCase().includes('invalid token') ||
            error.message?.toLowerCase().includes('token not found');
          if (isTokenError) {
            clearSession();
          } else {
            setUser(null);
            setProfile(null);
            setLoading(false);
          }
          return;
        }
        setUser(session?.user ?? null);
        if (session?.user) {
          fetchProfile(session.user.id);
        } else {
          setLoading(false);
        }
      });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'TOKEN_REFRESHED' && !session) {
          await clearSession();
          return;
        }
        if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
          setUser(null);
          setProfile(null);
          setLoading(false);
          return;
        }
        setUser(session?.user ?? null);
        if (session?.user) {
          await fetchProfile(session.user.id);
        } else {
          setProfile(null);
          setLoading(false);
        }
      },
    );

    return () => subscription.unsubscribe();
  }, []);

  const fetchProfile = async (userId: string, retries = 2) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        console.error('Profile fetch error:', error);
        if (retries > 0 && !data) {
          await new Promise((resolve) => setTimeout(resolve, 300));
          return fetchProfile(userId, retries - 1);
        }
        throw error;
      }

      if (!data) {
        const currentUser = await supabase.auth.getUser();
        if (currentUser.data.user) {
          setProfile({
            id: userId,
            email: currentUser.data.user.email || '',
            full_name:
              currentUser.data.user.user_metadata?.full_name || 'User',
            role:
              (currentUser.data.user.user_metadata?.role as Profile['role']) ||
              'customer',
            phone: currentUser.data.user.user_metadata?.phone || '',
            created_at: new Date().toISOString(),
          });
        }
      } else {
        setProfile(data as Profile);
      }
    } catch (error: any) {
      console.error('Error fetching profile:', error);
      setProfile({
        id: userId,
        email: '',
        full_name: 'User',
        role: 'customer',
        created_at: new Date().toISOString(),
      });
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
      if (!data.user) throw new Error('Sign in failed. Please try again.');
    } catch (error: any) {
      console.error('Sign in error:', error);
      if (error.message?.includes('Invalid login credentials')) {
        throw new Error(
          'Invalid email or password. Please check your credentials and try again.',
        );
      }
      if (error.message?.includes('Email not confirmed')) {
        throw new Error('Please verify your email address before signing in.');
      }
      throw new Error(error.message || 'Sign in failed. Please try again.');
    }
  };

  const signUp = async (
    email: string,
    password: string,
    fullName: string,
    role: string,
    phone?: string,
  ) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { full_name: fullName, role, phone: phone || '' },
          emailRedirectTo: `${window.location.origin}/`,
        },
      });
      if (error) throw error;
      if (!data.user) throw new Error('Failed to create account. Please try again.');
      if (data.user && !data.session) throw new Error('CONFIRMATION_REQUIRED');
    } catch (error: any) {
      console.error('Sign up error:', error);
      if (error.message === 'CONFIRMATION_REQUIRED') throw error;
      if (
        error.message?.includes('already registered') ||
        error.message?.includes('already been registered')
      ) {
        throw new Error('This email is already registered. Please sign in instead.');
      }
      if (error.message?.includes('Password should be at least')) {
        throw new Error('Password must be at least 6 characters long.');
      }
      throw new Error(error.message || 'Failed to create account. Please try again.');
    }
  };

  const signInWithGoogle = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/`,
          queryParams: { access_type: 'offline', prompt: 'consent' },
        },
      });
      if (error) throw error;
    } catch (error: any) {
      throw new Error(error.message || 'Failed to sign in with Google. Please try again.');
    }
  };

  const signOut = async () => {
    try {
      setUser(null);
      setProfile(null);
      supabase.auth.signOut().catch((err) => console.error('Sign out API error:', err));
    } catch {
      setUser(null);
      setProfile(null);
    }
  };

  return (
    <AuthContext.Provider
      value={{ user, profile, loading, signIn, signUp, signInWithGoogle, signOut }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
